import InstrumentCategory from "../model/InstrumentCategory";
import InterestType from "../model/InterestType";
import ValidationRulesProvider, { LETTER_TO_CODE } from "./validationRules";
import MaturityTypes from "../model/MaturityTypes";
import { matchesPattern } from "./cfiMatcher";
import { SPECIFIC_DATE_INDICATOR } from "../isin/MaturityDateInput";
import { isSkipInterestTypeCrossValidationCase } from "./optionalFieldsSpecialCases";

const validationRules = ValidationRulesProvider();
const cfiInterestTypeRules: Map<string, string[]> = validationRules.getInterestTypeRules();
const cfiMaturityDateRules: Map<string, string[]> = validationRules.getMaturityDateRules();

function isStartingWith(allowedPrefixes, cfiCode) {
    return allowedPrefixes.findIndex((cfiCodeSearch) => {
        return cfiCode.startsWith(cfiCodeSearch);
    }, cfiCode) === -1;
}

// Todo : AS translate.

/** Normalize any CFI to uppercase trimmed string. */
function normalizeCfi(cfiCode: string | undefined | null): string {
    return (cfiCode ?? "").toString().trim().toUpperCase();
}

type RulesMap = Map<string, string[]>;            // e.g. { "DT": ["D", "DA", "DB", ...], ... }
type SecondsByFirst = Map<string, Set<string>>;   // e.g. { "D" -> {"A","B",...} }
type WildcardSet = Set<string>;                   // e.g. { "D" } means any second char allowed

/** Build second-char rules from raw CFI prefix rules (JSON is the single source of truth). */
function buildSecondCharRules(rules: RulesMap): {
    allowedSecondsByFirst: SecondsByFirst;
    wildcardFirsts: WildcardSet;
} {
    const allowedSecondsByFirst: SecondsByFirst = new Map();
    const wildcardFirsts: WildcardSet = new Set();

    rules.forEach((prefixes) => {
        (prefixes || []).forEach((raw) => {
            if (!raw) return; // ignore "", null, undefined
            const p = raw.toUpperCase().trim();
            if (p.length === 0) return; // ignore empty strings

            const first = p.charAt(0);

            if (p.length === 1) {
                // one-letter rule ⇒ any second char allowed for this first letter
                wildcardFirsts.add(first);
                return;
            }

            const second = p.charAt(1);
            if (!allowedSecondsByFirst.has(first)) {
                allowedSecondsByFirst.set(first, new Set());
            }
            allowedSecondsByFirst.get(first)!.add(second);
        });
    });

    return { allowedSecondsByFirst, wildcardFirsts };
}

/**
 * if a first letter is known in LETTER_TO_CODE but has
 * no explicit rule (no seconds and not wildcard), allow any second char.
 */
function augmentWithFirstLetterFallback(
    allowedSecondsByFirst: SecondsByFirst,
    wildcardFirsts: WildcardSet
): void {
    Object.keys(LETTER_TO_CODE).forEach((rawFirst) => {
        const first = rawFirst.toUpperCase();
        if (!wildcardFirsts.has(first) && !allowedSecondsByFirst.has(first)) {
            wildcardFirsts.add(first);
        }
    });
}

/** Returns true if (first, second) is allowed per rules (including wildcard firsts). */
function isSecondAllowed(
    first: string,
    second: string,
    allowedSecondsByFirst: SecondsByFirst,
    wildcardFirsts: WildcardSet
): boolean {
    if (wildcardFirsts.has(first)) return true;
    const seconds = allowedSecondsByFirst.get(first);
    return !!seconds && seconds.has(second);
}

/** Human-friendly list of allowed prefixes for error text. */
function formatAllowedPrefixes(
    first: string,
    allowedSecondsByFirst: SecondsByFirst,
    wildcardFirsts: WildcardSet
): string {
    if (wildcardFirsts.has(first)) return `any (starts with '${first}')`;

    const seconds = allowedSecondsByFirst.get(first);
    if (!seconds || seconds.size === 0) return "—";

    return Array.from(seconds)
        .sort()
        .map((s) => `${first}${s}`)
        .join(", ");
}

/** Extracts first and second char (assumes length >= 2). */
function firstTwoChars(code: string): { first: string; second: string } {
    return { first: code.charAt(0), second: code.charAt(1) };
}

/**
 * NEW helper:
 * Collect all allowed first characters for a nicer error message
 * when the first character of the given CFI code is not even valid.
 */
function collectAllowedFirstChars(
    allowedSecondsByFirst: SecondsByFirst,
    wildcardFirsts: WildcardSet
): string[] {
    const firsts = new Set<string>();

    // any first char that appears with explicit seconds
    allowedSecondsByFirst.forEach((_seconds, first) => {
        firsts.add(first);
    });

    // any first char that is wildcard (meaning "first* is allowed")
    wildcardFirsts.forEach((first) => {
        firsts.add(first);
    });

    return Array.from(firsts).sort();
}

export function validateCfiCodeInstrumentCategory(
    cfiCode: string,
    _category?: string
): string | undefined {
    const code = normalizeCfi(cfiCode);
    if (code.length < 2) return undefined;

    const rules = validationRules.getCfi2015ValidationRules();

    const { allowedSecondsByFirst, wildcardFirsts } = buildSecondCharRules(rules);

    // augmentWithFirstLetterFallback(allowedSecondsByFirst, wildcardFirsts);

    const { first, second } = firstTwoChars(code);

    const allowedPrefixesForFirst = formatAllowedPrefixes(
        first,
        allowedSecondsByFirst,
        wildcardFirsts
    );

    let firstHasAnyValidContinuation = false;
    if (wildcardFirsts.has(first)) {
        firstHasAnyValidContinuation = true;
    } else if (allowedSecondsByFirst.has(first)) {
        const seconds = allowedSecondsByFirst.get(first);
        if (seconds && seconds.size > 0) {
            firstHasAnyValidContinuation = true;
        }
    }

    if (!firstHasAnyValidContinuation || allowedPrefixesForFirst === "—") {
        const allowedFirstChars = collectAllowedFirstChars(
            allowedSecondsByFirst,
            wildcardFirsts
        ).join(", ");

        return (
            `CFI code '${code}' is inconsistent: first character '${first}' is not allowed. ` +
            `Allowed starting characters are: ${allowedFirstChars}.`
        );
    }

    if (!isSecondAllowed(first, second, allowedSecondsByFirst, wildcardFirsts)) {
        return (
            `CFI code '${code}' is inconsistent: for CFI code starting with '${first}', ` +
            `the second character '${second}' is not allowed. ` +
            `Allowed ISO 10962:2019 CFI prefixes for '${first}': ${allowedPrefixesForFirst}.`
        );
    }
    return undefined;
}

export function validateCfiCodeInterestType(cfiCode: string, interestTypeValue: string, instrumentCategoryValue): string {

    if (ignoreCfiInterestTypeValidation(cfiCode, interestTypeValue, instrumentCategoryValue)) {
        return undefined;
    }

    const interestType = InterestType[interestTypeValue];
    const pattern: string[] = cfiInterestTypeRules.get(interestTypeValue);

    if (matchesPattern(cfiCode, pattern)) {
        return undefined
    }

    return 'The Type of Interest ' + interestTypeValue + ' (' + interestType + ') is not consistent with the CFI Code provided. (Allowed : ' + pattern + ')';
}

export function validateCfiCodeStartingWithDM(cfiCode: string, instrumentCategoryValue): string {
    if(!cfiCode || !cfiCode.startsWith('DM')){
        return undefined
    }

    const instrumentCategory = InstrumentCategory[instrumentCategoryValue];

    if(instrumentCategory !== InstrumentCategory.DT && instrumentCategory !== InstrumentCategory.SP){
        return undefined
    }

    // For CFI codes that begin with DM, the only valid characters for the third position are B, P, M and X.
    if(!matchesPattern(cfiCode, ["DMB***","DMP***","DMM***"])){
        return 'For CFI codes that begin with DM, the only valid characters for the third position are B, P, M and X.!'
    }
    return undefined
}

function ignoreCfiInterestTypeValidation(cfiCode: string, interestTypeValue: string, instrumentCategoryValue): Boolean {
    if (!cfiCode || !interestTypeValue || cfiCode.length < 2 || cfiCode.length > 6) {
        return true;
    }

    const instrumentCategory = InstrumentCategory[instrumentCategoryValue];
    // Only crossvalidate for Debt and Structured Products.
    if (InstrumentCategory.DT !== instrumentCategory && InstrumentCategory.SP !== instrumentCategory) {
        return true

    }

    // In case of Structured Products only validate when CFI starting with D, indicating a Debt.
    if (InstrumentCategory.SP === instrumentCategory && !cfiCode.startsWith('D')) {
        return true
    }

    // Only check against valid Instrument types. There are old/invalid values in the DB like 'N'
    if (!Object.keys(InterestType).includes(interestTypeValue)) {
        return true;
    }

    if (isSkipInterestTypeCrossValidationCase(cfiCode)){
        return true;
    }

    return false;
}

export function validateCfiBelongsToDsb(cfiCode: string): string | undefined {
    const code = normalizeCfi(cfiCode);
    if (!code) return undefined;

    const first = code.charAt(0);
    if (first === "J" || first === "K" || first === "L" || first === "S") {
        return "anna.validation.cfiBelongsToDsb";
    }
    return undefined;
}

export function validateCfiCodeMaturityDate(cfiCode: string, instrumentCategory: string, maturityDateCode: string, interestType: string): string {

    if (!cfiCode || !interestType || !instrumentCategory || maturityDateCode === SPECIFIC_DATE_INDICATOR) {
        return undefined;
    }

    if(InstrumentCategory[instrumentCategory] !== InstrumentCategory.DT && InstrumentCategory[instrumentCategory] !== InstrumentCategory.SP){
        return undefined
    }

    if(InstrumentCategory[instrumentCategory] === InstrumentCategory.SP && !cfiCode.startsWith('D')){
        return undefined
    }

    const pattern: string[] = cfiMaturityDateRules.get(maturityDateCode);

    if(!pattern){
        return undefined
    }

    if(matchesPattern(cfiCode, pattern)){
        return undefined
    }

    const maturityType = MaturityTypes[maturityDateCode];

    return 'The Maturity Date ' + maturityDateCode + ' (' + maturityType + ') is not consistent with the CFI Code provided. (Allowed : ' + pattern + ')';
}

export function validateXtIsinCfiStartsWithT(
    isin: string | undefined | null,
    cfiCode: string | undefined | null
): string | undefined {
    const normalizedIsin = (isin ?? "").toString().trim().toUpperCase();
    const normalizedCfi = normalizeCfi(cfiCode);

    if (!normalizedIsin.startsWith("XT")) {
        return undefined;
    }

    if (!normalizedCfi) {
        return undefined;
    }

    if (!normalizedCfi.startsWith("T")) {
        return "For XT ISIN records, the CFI Code must start with 'T'.";
    }

    return undefined;
}
